import { Account } from 'api/interfaces'
import { TempKeyForAccountAndInvoiceHandle } from 'api/interfaces/TempKeyForAccountAndInvoiceHandle'
import { defaultTransformFn, IBaseQuery, platformApi } from 'api/rtkQueryApi/platform/platformApi'
import { CreateAccountRequest, UpdateAccountRequest } from 'api/rtkQueryApi/requestModels/AccountModels'

import { ReserveSettings } from '../../interfaces/ReserveSettings'

export interface IGetTempKeyForAccountAndInvoiceHandleQuery extends IBaseQuery {
  invoiceHandle: string
}

const accountsTags = platformApi.enhanceEndpoints({ addTagTypes: ['Accounts', 'Account', 'TempKeyForAccountAndInvoiceHandle', 'BillingProvider'] })

const accountsApi = accountsTags.injectEndpoints({
  endpoints: build => ({
    getAccounts: build.query<Account[], IBaseQuery>({
      query: () => ({
        url: `accounts`,
        method: 'GET',
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      providesTags: result => (result ? [...result.map(({ id }) => ({ type: 'Account' as const, id: id })), { type: 'Accounts' }] : [{ type: 'Accounts' }])
    }),
    getAccount: build.query<Account, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}`,
        method: 'GET',
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      providesTags: result => (result ? [{ type: 'Account', id: result.id }] : [])
    }),
    updateAccount: build.mutation<Account, { accountId: string; request: UpdateAccountRequest }>({
      query: ({ accountId, request }) => ({
        url: `accounts/${accountId}`,
        method: 'PUT',
        isContext: true,
        data: request
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => (result ? [{ type: 'Account', id: result.id }, { type: 'Accounts' }] : [])
    }),
    createAccount: build.mutation<Account, CreateAccountRequest>({
      query: createAccountRequest => ({
        url: `accounts`,
        method: 'POST',
        data: createAccountRequest,
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: () => [{ type: 'Accounts' }]
    }),
    getTempKeyForAccountAndInvoiceHandle: build.query<TempKeyForAccountAndInvoiceHandle, IGetTempKeyForAccountAndInvoiceHandleQuery>({
      query: ({ invoiceHandle, isAgency }) => {
        return {
          url: `accounts/${invoiceHandle}/tempKey`,
          method: 'GET',
          isContext: true,
          isAgency: isAgency
        }
      },
      transformResponse: (response: { data: string }, meta, { invoiceHandle }) => {
        return {
          tempKey: response.data,
          invoiceHandle
        }
      }
    }),
    setAccountAgencyReserveSettings: build.mutation<Account, { accountId: string; agencyReserveSettings: ReserveSettings }>({
      query: ({ accountId, agencyReserveSettings }) => ({
        url: `accounts/${accountId}/agencyReserveSettings`,
        method: 'POST',
        isContext: true,
        data: {
          inheritAgencyReserve: agencyReserveSettings.inheritAgencyReserveRate,
          reserveRate: agencyReserveSettings.reserveRate
        }
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => (result ? [{ type: 'Account', id: result.id }, { type: 'Accounts' }] : [])
    })
  }),
  overrideExisting: false
})

export const { useGetAccountsQuery, useCreateAccountMutation, useLazyGetTempKeyForAccountAndInvoiceHandleQuery, useUpdateAccountMutation, useSetAccountAgencyReserveSettingsMutation } = accountsApi
